import { useEffect, useRef, useState } from "react";
import { LockKey, LockKeyOpen } from "@phosphor-icons/react";

const HOLD_MS = 3000;

// Toddler-proof screen lock. A full-viewport transparent shield swallows every tap,
// swipe, click and key press so an infant can't pause/skip/exit. Unlock requires a
// deliberate 3-second long-press on the floating padlock.
export function ScreenLock({ onUnlock }) {
  const [progress, setProgress] = useState(0);
  const [showHint, setShowHint] = useState(true);
  const timer = useRef(null);
  const raf = useRef(null);
  const startedAt = useRef(0);

  // Auto-hide the instructions after 3s so they don't linger over the video.
  useEffect(() => {
    const t = setTimeout(() => setShowHint(false), 3000);
    return () => clearTimeout(t);
  }, []);

  // Block all keyboard / D-pad input (incl. media keys + SpatialNav) while locked.
  useEffect(() => {
    const swallow = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    window.addEventListener("keydown", swallow, true);
    window.addEventListener("keyup", swallow, true);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", swallow, true);
      window.removeEventListener("keyup", swallow, true);
      document.body.style.overflow = "";
    };
  }, []);

  const cleanup = () => {
    clearTimeout(timer.current);
    cancelAnimationFrame(raf.current);
    setProgress(0);
  };

  const begin = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startedAt.current = Date.now();
    const tick = () => {
      const p = Math.min(1, (Date.now() - startedAt.current) / HOLD_MS);
      setProgress(p);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    timer.current = setTimeout(() => {
      cleanup();
      onUnlock();
    }, HOLD_MS);
  };

  const end = (e) => {
    e.preventDefault();
    e.stopPropagation();
    cleanup();
  };

  const held = progress > 0;

  return (
    <div
      data-testid="screen-lock-shield"
      className="fixed inset-0 z-[100] bg-black/5 select-none"
      onClickCapture={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onContextMenu={(e) => e.preventDefault()}
      style={{ touchAction: "none" }}
    >
      {(showHint || held) && (
        <div className="absolute bottom-28 left-1/2 -translate-x-1/2 text-center">
          <p
            data-testid="screen-lock-hint"
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 bg-black/50 backdrop-blur-md border border-white/10 rounded-full px-4 py-2"
          >
            {held ? "Keep holding…" : "Screen locked · hold the lock 3s to unlock"}
          </p>
        </div>
      )}

      <button
        data-testid="screen-lock-unlock"
        aria-label="Hold to unlock"
        onMouseDown={begin}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={begin}
        onTouchEnd={end}
        onTouchCancel={end}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 h-20 w-20 rounded-full flex items-center justify-center focus:outline-none"
        style={{
          background: `conic-gradient(#38bdf8 ${progress * 360}deg, rgba(255,255,255,0.14) 0deg)`,
        }}
      >
        <span className="absolute inset-[5px] rounded-full bg-[#0a0a0a] border border-white/10 flex items-center justify-center">
          {held ? (
            <LockKeyOpen size={30} weight="duotone" className="text-sky-400" />
          ) : (
            <LockKey size={30} weight="duotone" className="text-white/85" />
          )}
        </span>
      </button>
    </div>
  );
}
